import * as React from 'react'
import { FieldValues, SubmitErrorHandler } from 'react-hook-form'

import { sortArray } from '~logic/helpers/arrayUtils/sort'
import { isDefined } from '~logic/helpers/isDefined'
import { objectKeys } from '~logic/helpers/objectUtils/object'

/**
 * Adds extra features to the form error handling (focuses the invalid input).
 *
 * will can be used as the onError callback called by `handleSubmit` on submit attempt,
 * with all of the fields that was not valid, and validation reasons & messages.
 * It is also possible to call this function with validation errors obtained from `formState`.
 */
export const useOnValidationError = <TForm extends FieldValues>() => {
  const onValidationError: SubmitErrorHandler<TForm> = React.useCallback((validations) => {
    // the errors should be passed to related fields automagically
    // and there they are supposed to be presented to the customer
    // this function only handles scrolling to the first invalid field from the form top
    const invalidFieldElements = objectKeys(validations)
      .map((name) => document.getElementById(String(name)))
      .filter(isDefined)

    const topElement = getUppermostElement(invalidFieldElements)

    if (!topElement) {
      return
    }

    topElement.scrollIntoView({ behavior: 'smooth', block: 'center' })

    // setTimeout hack to make it work also as the `onError` callback of the `handleSubmit` from 'react-hook-form'
    setTimeout(() => topElement.focus({ preventScroll: true }))
  }, [])

  return onValidationError
}

//* HELPERS

// sorts an array of `HTMLElements` by their position on the page (top to bottom)
// returns the first element or `null` (if the array is empty)
const getUppermostElement = (elements: RoA<HTMLElement>) => {
  if (elements.length === 0) {
    return null
  }

  if (elements.length === 1) {
    return elements[0]
  }

  const sortedElements = sortArray<HTMLElement>(sortElementsByTopPosition)(elements)

  return sortedElements[0]
}

// sorting function,
// sorts elements by their position within the page, the ones on top first
const sortElementsByTopPosition = (elementA: HTMLElement, elementB: HTMLElement) => {
  return elementA.getBoundingClientRect().top - elementB.getBoundingClientRect().top
}
